/* eslint-disable no-nested-ternary */
import { Grid, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { formatPrice } from '~/util/format';
import getAssessmentDatesByDate from '~/util/getAssessmentDatesByDate';
import { NV } from '~/util/brands';
import PanelBody from './PanelBody';
import CelebrationContainer from './CelebrationContainer';
import ChangeToChartButton from '../ChangeToChart';
import RecalculatedQuotaCheckbox from './RecalculatedQuotaCheckbox';
import * as Style from './styles';

function MainPanelCard({
    balanceText,
    salesNumber,
    monthlySalesNumber,
    quota,
    salesPercentage,
    aggregatedOrdersByPeriod,
    handleFlipped,
    showIcon,
    hideSuperQuota,
    isVendedorView,
}) {
    const { finalDate, period } = useSelector(state => state.status);
    const userProfile = useSelector(state => state.user.profile);

    const [daysLeftInPeriod, setDaysLeftInPeriod] = useState(0);
    const [showRecalculatedQuota, setShowRecalculatedQuota] = useState(false);
    const [isLastDay, setIsLastDay] = useState(false);
    const [hideDaysLeftMessage, setHideDaysLeftMessage] = useState(false);

    const isTodayBeforeFinalDate = moment().isBefore(moment(finalDate), 'day');

    const monthlyQuotas = quota?.periodQuotas?.find(p => p.period === 'month');
    const dailyQuotas = quota?.periodQuotas?.find(p => p.period === 'day');

    const quotaInCurrentMonth = monthlyQuotas?.quota;
    const superQuotaMultiplier = monthlyQuotas?.superQuotaMultiplier || 1;
    const hyperQuotaMultiplier =
        monthlyQuotas?.hiperQuotaMultiplier || superQuotaMultiplier;

    const dailyQuotaValue = (() => {
        if (dailyQuotas?.quota) return dailyQuotas.quota;
        if (quota?.dailyQuotas && finalDate) {
            const dateKey = moment(finalDate).format('YYYY-MM-DD');
            return quota.dailyQuotas[dateKey] ?? quota?.totalQuotaAsNumber;
        }
        return quota?.totalQuotaAsNumber;
    })();

    const monthlyAggregatedOrders = aggregatedOrdersByPeriod?.find(
        p => p.period === 'month'
    );
    const dailyAggregatedOrders = aggregatedOrdersByPeriod?.find(
        p => p.period === 'day'
    );

    const salesFromMonthlyPeriodData =
        monthlyAggregatedOrders?.sales?.salesGroupedByChannelAndDay?.total
            ?.value +
        monthlyAggregatedOrders?.sales?.salesGroupedByChannelAndDay?.returns
            ?.value;

    const salesFromDailyPeriodData =
        dailyAggregatedOrders?.sales?.salesGroupedByChannelAndDay?.total
            ?.value +
        dailyAggregatedOrders?.sales?.salesGroupedByChannelAndDay?.returns
            ?.value;

    // Usa dados do periodSales quando disponíveis (somente vendedor possui periodSales);
    // Caso contrário, usa salesNumber (em centavos → /100)
    const salesInCurrentPeriod = Number.isFinite(salesFromMonthlyPeriodData)
        ? salesFromMonthlyPeriodData
        : salesNumber / 100;

    const salesToday = Number.isFinite(salesFromDailyPeriodData)
        ? salesFromDailyPeriodData
        : period === 'day'
        ? salesNumber / 100
        : 0;

    // Acumulado mensal real para cálculo de cota recalculada
    // Vendedor: usa periodSales (salesFromMonthlyPeriodData).
    // Gerente/fallback: usa monthlySalesNumber, que vem do monthSalesRef
    const monthlyAccumulatedSales = Number.isFinite(salesFromMonthlyPeriodData)
        ? salesFromMonthlyPeriodData
        : (monthlySalesNumber ?? salesNumber) / 100;

    // O check de conquista usa sempre a cota cheia do mês (quotaInCurrentMonth).
    const hasAchievedMonthlyQuota =
        !!quotaInCurrentMonth && salesInCurrentPeriod >= quotaInCurrentMonth;

    const hasAchievedMonthlySuperQuota =
        !!quotaInCurrentMonth &&
        superQuotaMultiplier > 1 &&
        salesInCurrentPeriod >= quotaInCurrentMonth * superQuotaMultiplier;

    const hasAchievedMonthlyHyperQuota =
        !!quotaInCurrentMonth &&
        hyperQuotaMultiplier > superQuotaMultiplier &&
        salesInCurrentPeriod >= quotaInCurrentMonth * hyperQuotaMultiplier;

    const shouldShowCongratulationMessage =
        period === 'month' &&
        (hasAchievedMonthlyQuota ||
            hasAchievedMonthlySuperQuota ||
            hasAchievedMonthlyHyperQuota);

    const hasAchievedDailyQuota =
        period === 'day' && !!dailyQuotaValue && salesToday >= dailyQuotaValue;

    const hasAchievedDailySuperQuota =
        hasAchievedDailyQuota &&
        superQuotaMultiplier > 1 &&
        salesToday >= dailyQuotaValue * superQuotaMultiplier;

    const hasAchievedDailyHyperQuota =
        hasAchievedDailySuperQuota &&
        hyperQuotaMultiplier > superQuotaMultiplier &&
        salesToday >= dailyQuotaValue * hyperQuotaMultiplier;

    const getQuotaAccumulatedUntilToday = () => {
        if (period !== 'day') return monthlyQuotas?.quotaUntilEndDate;

        if (monthlyQuotas?.quotaUntilRestOfMonth && quotaInCurrentMonth) {
            const quotaBeforeToday =
                quotaInCurrentMonth - monthlyQuotas.quotaUntilRestOfMonth;
            return quotaBeforeToday + (dailyQuotaValue || 0);
        }

        return monthlyQuotas?.quotaUntilEndDate;
    };

    const quotaAccumulatedUntilToday = getQuotaAccumulatedUntilToday();

    // Compara acumulado do mês até hoje vs cota acumulada até hoje (ambos os modos).
    // No modo 'day': monthlyAccumulatedSales = vendido desde o início do período;
    // quotaAccumulatedUntilToday = cota esperada do primeiro dia até hoje.
    // Assim o usuário vê o quanto está à frente ou atrás do ritmo necessário para bater a cota.
    const salesDifference =
        monthlyAccumulatedSales - (quotaAccumulatedUntilToday || 0);

    const salesExtra = {
        value: salesDifference,
        color: salesDifference > 0 ? '#1BB55C' : '#E26257',
        valueAsPrice: formatPrice(salesDifference),
        secondaryText: salesDifference > 0 ? 'mais' : 'menos',
    };

    // Usa monthlyAccumulatedSales para garantir que o recalculo reflita o mês inteiro, e não apenas o dia selecionado.
    // Evita cota recalculada negativa quando já vendeu mais que a cota do mês.
    const remainingToSell = Math.max(
        0,
        quotaInCurrentMonth - monthlyAccumulatedSales
    );
    const recalculatedQuota =
        daysLeftInPeriod > 0 ? remainingToSell / daysLeftInPeriod : undefined;

    // NV não exibe a barra de supercota após atingir a cota
    const NVQuotaBarCondition =
        userProfile?.marcaFilial === NV &&
        period === 'month' &&
        shouldShowCongratulationMessage &&
        !hideSuperQuota;

    const showDaysLeftFooter =
        period !== 'day' &&
        !hideDaysLeftMessage &&
        daysLeftInPeriod >= 0 &&
        daysLeftInPeriod <= 31 &&
        !showRecalculatedQuota;

    const shouldShowMainPanelBody =
        !NVQuotaBarCondition &&
        !(isVendedorView && shouldShowCongratulationMessage);

    // Checkbox de cota recalculada aparece no modo dia quando é hoje,
    // há dados de vendas do mês disponíveis E existe cota mensal > 0.
    const hasMonthlyQuotaData =
        Number.isFinite(salesFromMonthlyPeriodData) ||
        ((monthlySalesNumber ?? salesNumber) > 0 && !!quotaInCurrentMonth);
    const canShowRecalculatedQuotaCheckbox =
        period === 'day' &&
        moment().isSame(finalDate, 'day') &&
        hasMonthlyQuotaData &&
        quotaInCurrentMonth > 0;

    const getDayMode = () => {
        if (
            hasAchievedDailyHyperQuota &&
            hyperQuotaMultiplier > superQuotaMultiplier
        )
            return 'hyperQuota';
        if (hasAchievedDailySuperQuota && superQuotaMultiplier > 1)
            return 'superQuota';
        return 'quota';
    };

    const renderDayProgressBar = () => {
        const currentDayMode = getDayMode();
        return (
            <PanelBody
                key={`day-${currentDayMode}`}
                quota={quota}
                salesNumber={salesNumber}
                hasAchievedMonthlySuperQuota={false}
                shouldShowCongratulationsMessage={false}
                showRecalculatedQuota={showRecalculatedQuota}
                salesPercentage={salesPercentage}
                recalculatedQuota={recalculatedQuota}
                isLastDay={isLastDay}
                isTodayBeforeFinalDate={isTodayBeforeFinalDate}
                dayMode={currentDayMode}
                dailyQuotaValue={dailyQuotaValue}
                superQuotaMultiplier={superQuotaMultiplier}
                hyperQuotaMultiplier={hyperQuotaMultiplier}
            />
        );
    };

    useEffect(() => {
        if (['custom', 'week'].includes(period)) {
            setHideDaysLeftMessage(true);
            setIsLastDay(false);
            return;
        }

        if (!finalDate) return;

        const assessmentDates = getAssessmentDatesByDate(
            moment(finalDate),
            userProfile?.marcaFilial
        );
        if (!assessmentDates) return;

        const daysLeft = assessmentDates.endDate.diff(
            moment().startOf('day'),
            'days'
        );
        setDaysLeftInPeriod(daysLeft);

        if (period === 'month') {
            setIsLastDay(
                moment(finalDate).isSame(assessmentDates.endDate, 'day')
            );
            const todayIsInPeriod =
                moment().isSameOrAfter(assessmentDates.startDate, 'day') &&
                moment().isSameOrBefore(assessmentDates.endDate, 'day');
            setHideDaysLeftMessage(!todayIsInPeriod);
        }

        if (period === 'day') {
            setIsLastDay(
                moment().isSame(assessmentDates.endDate, 'day') &&
                    moment(finalDate).isSame(assessmentDates.endDate, 'day')
            );
            setHideDaysLeftMessage(!moment().isSame(finalDate, 'day'));
        }
    }, [finalDate, period, userProfile?.marcaFilial, isTodayBeforeFinalDate]);

    const monthPanelBodyProps = {
        quota,
        salesNumber,
        hasAchievedMonthlySuperQuota,
        shouldShowCongratulationsMessage: shouldShowCongratulationMessage,
        showRecalculatedQuota,
        salesPercentage,
        recalculatedQuota,
        isLastDay,
        isTodayBeforeFinalDate,
    };

    return (
        <Style.PanelCardContainer
            container
            primary={!!shouldShowCongratulationMessage}
        >
            <ChangeToChartButton
                showIcon={showIcon}
                handleFlipped={handleFlipped}
            />

            <Style.BalanceTextContainer
                item
                primary={!!shouldShowCongratulationMessage}
            >
                {balanceText}
            </Style.BalanceTextContainer>

            <Style.InnerPanelCardContainer
                item
                container
                primary={period === 'day'}
            >
                <Grid item style={{ width: '95%' }}>
                    {shouldShowCongratulationMessage && period === 'month' && (
                        <CelebrationContainer
                            salesNumber={salesNumber}
                            monthlyQuotas={monthlyQuotas}
                        />
                    )}

                    {period === 'day' && renderDayProgressBar()}

                    {period !== 'day' && shouldShowMainPanelBody && (
                        <PanelBody {...monthPanelBodyProps} />
                    )}

                    {!shouldShowCongratulationMessage &&
                        period === 'month' &&
                        isTodayBeforeFinalDate &&
                        !NVQuotaBarCondition && (
                            <PanelBody
                                {...monthPanelBodyProps}
                                secondaryProgressView
                            />
                        )}

                    {canShowRecalculatedQuotaCheckbox && (
                        <RecalculatedQuotaCheckbox
                            showRecalculatedQuota={showRecalculatedQuota}
                            setShowRecalculatedQuota={setShowRecalculatedQuota}
                        />
                    )}
                </Grid>

                {showRecalculatedQuota && (
                    <Grid container justifyContent="space-between">
                        <Style.DividerComponent />
                        <Typography
                            style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                textAlign: 'left',
                                display: 'inline',
                            }}
                        >
                            Vendido a {salesExtra.secondaryText} no período
                        </Typography>
                        <Typography
                            style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                textAlign: 'right',
                                paddingLeft: '8px',
                                color: salesExtra.color,
                                display: 'inline',
                            }}
                        >
                            {salesExtra.valueAsPrice}
                        </Typography>
                    </Grid>
                )}

                {showDaysLeftFooter && (
                    <Grid container justifyContent="center">
                        <Style.DividerComponent />
                        <Style.FooterTypography>
                            {daysLeftInPeriod === 0 ? (
                                <strong>Último dia do período.</strong>
                            ) : (
                                <>
                                    {daysLeftInPeriod === 1
                                        ? 'Falta'
                                        : 'Faltam'}{' '}
                                    <strong>{daysLeftInPeriod}</strong> dias
                                    para o fim do período.
                                </>
                            )}
                        </Style.FooterTypography>
                    </Grid>
                )}
            </Style.InnerPanelCardContainer>
        </Style.PanelCardContainer>
    );
}

export default MainPanelCard;
